import React from "react";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDPagination from "components/MDPagination";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

function Tags() {
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [editingId, setEditingId] = React.useState(null);
  const [error, setError] = React.useState("");
  const [tags, setTags] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [limit] = React.useState(10);
  const [totalPages, setTotalPages] = React.useState(1);
  const [totalTagsCount, setTotalTagsCount] = React.useState(0);
  const [reloadKey, setReloadKey] = React.useState(0);

  const token =
    localStorage.getItem("dsf_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    "";
  const apiBaseUrl = (
    process.env.REACT_APP_API_BASE_URL || "https://dsf-api.classicprogrammers.com/api/v1/admin"
  ).replace(/\/+$/, "");

  const authHeaders = React.useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const buildTagsListUrls = React.useCallback(
    (nextPage) => {
      const endpoint = `/tags?page=${nextPage}&limit=${limit}`;
      const requestUrls = [];
      if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);
      return requestUrls;
    },
    [apiBaseUrl, limit]
  );

  const buildTagByIdUrls = React.useCallback(
    (id) => {
      const endpoint = `/tags/${id}`;
      const requestUrls = [];
      if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);
      return requestUrls;
    },
    [apiBaseUrl]
  );

  const buildTagsCollectionUrls = React.useCallback(() => {
    const endpoint = "/tags";
    const requestUrls = [];
    if (apiBaseUrl) requestUrls.push(`${apiBaseUrl}${endpoint}`);
    return requestUrls;
  }, [apiBaseUrl]);

  React.useEffect(() => {
    const fetchTags = async () => {
      setIsLoading(true);
      const requestUrls = buildTagsListUrls(page);
      try {
        let response;
        for (const url of requestUrls) {
          try {
            response = await axios.get(url, { headers: authHeaders });
            break;
          } catch (_requestError) {
            // Try next url candidate.
          }
        }

        const payload = response?.data?.data || response?.data || {};
        const list = Array.isArray(payload.tags)
          ? payload.tags
          : Array.isArray(payload.docs)
          ? payload.docs
          : Array.isArray(payload.items)
          ? payload.items
          : [];
        const normalized = list.map((tag, index) => ({
          id: tag?._id || tag?.id || `${page}-${index}`,
          name: tag?.name || "Untitled tag",
          description: tag?.description || "",
        }));
        const pagination = payload.pagination || payload.meta || {};
        const resolvedTotal =
          Number(pagination.total || pagination.totalTags || payload.total || normalized.length) ||
          0;
        const resolvedTotalPages =
          Number(
            pagination.totalPages ||
              payload.totalPages ||
              (resolvedTotal > 0 ? Math.ceil(resolvedTotal / limit) : 1)
          ) || 1;

        setTags(normalized);
        setTotalTagsCount(resolvedTotal);
        setTotalPages(resolvedTotalPages);
      } catch (_error) {
        setTags([]);
        setTotalTagsCount(0);
        setTotalPages(1);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTags();
  }, [authHeaders, buildTagsListUrls, limit, page, reloadKey]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setEditingId(null);
    setError("");
  };

  const MAX_TAGS = 6;

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Tag name is required.");
      return;
    }

    const duplicateTag = tags.find(
      (t) => t.id !== editingId && t.name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (duplicateTag) {
      setError("Tag name already exists.");
      return;
    }

    if (!editingId && totalTagsCount >= MAX_TAGS) {
      setError(`Maximum ${MAX_TAGS} tags allowed. Delete an existing tag to add a new one.`);
      toast.error(`Maximum ${MAX_TAGS} tags allowed.`);
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingId) {
        const requestUrls = buildTagByIdUrls(editingId);
        for (const url of requestUrls) {
          try {
            await axios.patch(
              url,
              { name: trimmedName, description: description.trim() },
              { headers: authHeaders }
            );
            break;
          } catch (_requestError) {
            // Try next url candidate.
          }
        }
        toast.success("Tag updated successfully.");
      } else {
        const requestUrls = buildTagsCollectionUrls();
        for (const url of requestUrls) {
          try {
            await axios.post(
              url,
              { name: trimmedName, description: description.trim() },
              { headers: authHeaders }
            );
            break;
          } catch (_requestError) {
            // Try next url candidate.
          }
        }
        toast.success("Tag created successfully.");
      }
      resetForm();
      setReloadKey((prev) => prev + 1);
    } catch (_error) {
      setError("Request failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <MDTypography sx={{ color: "#000", fontSize: "0.9rem", fontWeight: 700 }}>
            TAG
          </MDTypography>
        ),
        accessor: "tag",
        align: "left",
      },
      {
        Header: (
          <MDTypography sx={{ color: "#000", fontSize: "0.9rem", fontWeight: 700 }}>
            DESCRIPTION
          </MDTypography>
        ),
        accessor: "description",
        align: "left",
      },
      {
        Header: (
          <MDTypography sx={{ color: "#000", fontSize: "0.9rem", fontWeight: 700 }}>
            ACTION
          </MDTypography>
        ),
        accessor: "action",
        align: "center",
      },
    ],
    []
  );

  const rows = React.useMemo(
    () =>
      tags.map((t) => ({
        tag: (
          <MDTypography variant="button" fontWeight="medium" color="text">
            {t.name}
          </MDTypography>
        ),
        description: (
          <MDTypography variant="caption" color="text">
            {t.description || "-"}
          </MDTypography>
        ),
        action: (
          <MDBox display="flex" alignItems="center" justifyContent="center" gap={1}>
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              onClick={async () => {
                const requestUrls = buildTagByIdUrls(t.id);
                let response;
                for (const url of requestUrls) {
                  try {
                    response = await axios.get(url, { headers: authHeaders });
                    break;
                  } catch (_requestError) {
                    // Try next url candidate.
                  }
                }
                const payload = response?.data?.data || response?.data || {};
                const tag = payload?.tag || payload?.data || payload;
                setEditingId(t.id);
                setName(tag?.name || t.name);
                setDescription(tag?.description || t.description);
              }}
            >
              Edit
            </MDButton>
            <MDButton
              variant="outlined"
              color="error"
              size="small"
              onClick={async () => {
                if (!window.confirm("Do you want to delete this tag?")) return;
                const requestUrls = buildTagByIdUrls(t.id);
                for (const url of requestUrls) {
                  try {
                    await axios.delete(url, { headers: authHeaders });
                    break;
                  } catch (_requestError) {
                    // Try next url candidate.
                  }
                }
                setReloadKey((prev) => prev + 1);
                toast.success("Tag deleted successfully.");
              }}
            >
              Delete
            </MDButton>
          </MDBox>
        ),
      })),
    [authHeaders, buildTagByIdUrls, tags]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="contained"
                borderRadius="lg"
                coloredShadow="none"
                sx={{ background: "rgba(229, 10, 70, 1)" }}
              >
                <MDTypography variant="h6" color="white">
                  Tag Creation & Editing
                </MDTypography>
              </MDBox>

              <MDBox p={3}>
                <MDBox mb={2}>
                  <MDTypography variant="button" color="text">
                    Admin can create, update, and remove tags used for user grouping and reporting.
                    Maximum {MAX_TAGS} tags allowed ({totalTagsCount}/{MAX_TAGS} used).
                  </MDTypography>
                </MDBox>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Tag name"
                      fullWidth
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={!editingId && totalTagsCount >= MAX_TAGS}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDInput
                      label="Description"
                      fullWidth
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      disabled={!editingId && totalTagsCount >= MAX_TAGS}
                    />
                  </Grid>
                  <Grid item xs={12} md={2} display="flex" alignItems="center">
                    <MDBox display="flex" gap={1} width="100%">
                      <MDButton
                        fullWidth
                        variant="gradient"
                        color="info"
                        disabled={isSubmitting || (!editingId && totalTagsCount >= MAX_TAGS)}
                        onClick={handleSubmit}
                        sx={{
                          background: "rgba(229, 10, 70, 1)",
                          "&:hover": { background: "rgba(229, 10, 70, 0.9)" },
                        }}
                      >
                        {isSubmitting ? "Saving..." : editingId ? "Update" : "Create"}
                      </MDButton>
                      {editingId ? (
                        <MDButton fullWidth variant="outlined" color="dark" onClick={resetForm}>
                          Cancel
                        </MDButton>
                      ) : null}
                    </MDBox>
                  </Grid>
                </Grid>
                {error ? (
                  <MDBox mt={1}>
                    <MDTypography variant="caption" color="error">
                      {error}
                    </MDTypography>
                  </MDBox>
                ) : null}

                <MDBox mt={3}>
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                  {isLoading ? (
                    <MDBox
                      py={2}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                    >
                      <CircularProgress size={18} sx={{ color: "rgba(229, 10, 70, 1)" }} />
                      <MDTypography variant="caption" color="text">
                        Loading tags...
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <MDBox
                      pt={2.5}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      gap={1}
                    >
                      <MDBox display="flex" flexDirection="column">
                        <MDTypography variant="caption" color="text" fontWeight="medium">
                          Page {page} of {totalPages} ({totalTagsCount} tags)
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          Showing up to {limit} tags per page
                        </MDTypography>
                      </MDBox>
                      <MDPagination
                        variant="gradient"
                        color="dark"
                        sx={{
                          "& .MuiPaginationItem-root": {
                            color: "rgba(229, 10, 70, 1)",
                            borderColor: "rgba(229, 10, 70, 0.35)",
                          },
                          "& .MuiPaginationItem-root.Mui-selected": {
                            backgroundColor: "rgba(229, 10, 70, 1) !important",
                            color: "#fff !important",
                          },
                        }}
                      >
                        <MDPagination
                          item
                          disabled={page <= 1}
                          onClick={() => setPage((p) => p - 1)}
                        >
                          <span>{"<"}</span>
                        </MDPagination>
                        {Array.from({ length: totalPages }, (_, index) => index + 1)
                          .slice(Math.max(0, page - 3), Math.max(0, page - 3) + 5)
                          .map((pageNumber) => (
                            <MDPagination
                              key={pageNumber}
                              item
                              active={pageNumber === page}
                              onClick={() => setPage(pageNumber)}
                            >
                              {pageNumber}
                            </MDPagination>
                          ))}
                        <MDPagination
                          item
                          disabled={page >= totalPages}
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                          <span>{">"}</span>
                        </MDPagination>
                      </MDPagination>
                    </MDBox>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <ToastContainer position="top-right" autoClose={3000} />
    </DashboardLayout>
  );
}

export default Tags;
